import { useLayoutEffect, useRef } from 'react';

import { mapCampaignAreasToDtoAreas } from 'utils/campaign/mappers';
import { CAMPAIGN_SELECTED_ROOT_REGION } from 'utils/constants/campaign';

import useDebounce from 'bloko/common/hooks/useDebounce';

import {
    computeAudienceReachPrediction,
    computeVacancyProfRoleAndReachPrediction,
} from 'components/CpcFlexibleCreate/PredictWindow/utils/actions';

import { selectCampaignExcludedRegions, selectCampaignRegions } from 'store/models/createCampaign';
import {
    selectVacancyProfRoleId,
    setIsPredictWindowReady,
    setPredictWindowInitialState,
} from 'store/models/predictWindow';
import { useDispatch, useSelector } from 'store/utils';

const DEBOUNCED_TIME = 500;

export const useDownloadPredictData = (): void => {
    const vacancyId = useSelector(
        (state) => state.createBanner.bannerVacancyId ?? state.createCampaign.campaignVacancyId
    );
    const vacancyProfRoleId = useSelector(selectVacancyProfRoleId);
    const dispatch = useDispatch();
    const {
        adGender: sex,
        adAgeTo,
        adAgeFrom,
        adSalary: salary,
        adProfessions: profRoleIdsSelectedByUser,
        adUnknownAge,
    } = useSelector((state) => state.createCampaign);
    const adRegions = useSelector(selectCampaignRegions);
    const adExcludedRegions = useSelector(selectCampaignExcludedRegions);
    const debouncedComputeAudienceReachPrediction = useRef(
        useDebounce(
            (props: Parameters<typeof computeAudienceReachPrediction>[number]) =>
                dispatch(computeAudienceReachPrediction(props)),
            DEBOUNCED_TIME
        )
    );

    useLayoutEffect(() => {
        if (!vacancyId) return void 0;

        dispatch(setIsPredictWindowReady(false));
        void dispatch(computeVacancyProfRoleAndReachPrediction({ vacancyId: vacancyId.toString() }));

        return () => {
            dispatch(setPredictWindowInitialState());
        };
    }, [vacancyId, dispatch]);

    useLayoutEffect(() => {
        if (!vacancyProfRoleId) return;

        debouncedComputeAudienceReachPrediction.current({
            sex,
            age: {
                maxValue: adAgeTo,
                minValue: adAgeFrom,
                includeAnonymousAge: adUnknownAge,
            },
            salaryRange: salary,
            areas: mapCampaignAreasToDtoAreas(
                { include: adRegions, exclude: adExcludedRegions },
                { default: { include: CAMPAIGN_SELECTED_ROOT_REGION } }
            ),
            profRoleIdsSelectedByUser,
            vacancyProfRoleId: vacancyProfRoleId.toString(),
        });
    }, [
        vacancyProfRoleId,
        sex,
        adAgeTo,
        adAgeFrom,
        salary,
        profRoleIdsSelectedByUser,
        adUnknownAge,
        adRegions,
        adExcludedRegions,
    ]);
};
